import { useState } from "react";
import { Link } from "react-router-dom";
import {ReactComponent as AllBook} from 'src/assets/author/allbook_author.svg';
import { motion } from "framer-motion";

interface Book {
    bookTitle: string;
    coverImage: string;
    bookId?: string;
    bookIntro?: string;
    likeCount?: number;
    viewCount?: number;
    keywords?: string[];
}

interface BookProps {
    bookData: Book[];
}

const BooksList = ({ bookData }: BookProps) => {
    const INITIAL_GRID_COUNT = 2;
    const [showAll, setShowAll] = useState(false);

    if (bookData.length === 0) return null;


    const firstBook = bookData[0] as any;
    const restBooks = bookData.slice(1);
    const displayedRest = showAll ? restBooks : restBooks.slice(0, INITIAL_GRID_COUNT);

    const coverSrc = (book: any) =>
        book.coverImage
            ? `data:image/jpeg;base64,${book.coverImage}`
            : `${process.env.PUBLIC_URL}/img/book_5.jpg`;

    return (
        <div className="flex flex-col gap-6 lg:gap-8">
            {/* Section title */}
            <div className="flex items-center gap-3">
                <AllBook />
                <span className="text-xl lg:text-2xl font-bold text-[#101828]">모든 작품</span>
                <span className="text-lg lg:text-[22px] font-extrabold text-[#19cf7a]">{bookData.length}</span>
            </div>

            {/* Featured first book */}
            <motion.div 
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}
                className="bg-white border border-[#e5e7eb] rounded-3xl shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] flex flex-row hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300 cursor-pointer"
                style={{ padding: '25px 28px 20px 28px' }}>
                {/* Cover with gradient + keyword overlay */}
                <Link
                    to={`/book/bookdetails/${firstBook.bookId}`}
                    className="relative h-[287px] flex-shrink-0 block group overflow-hidden rounded-2xl"
                >
                    <img
                        className="h-full w-auto items-center object-cover transition-transform duration-300"
                        src={coverSrc(firstBook)}
                        alt={firstBook.bookTitle}
                    />
                    {firstBook.keywords && firstBook.keywords.length > 0 && (
                        <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5">
                            {firstBook.keywords.map((kw: string, i: number) => (
                                <span
                                    key={i}
                                    className="bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.2)] text-white text-xs lg:text-[15px] font-semibold rounded-full px-3 py-1.5 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)]"
                                >
                                    #{kw}
                                </span>
                            ))}
                        </div>
                    )}
                </Link>

                {/* Info panel */}
                <div className="flex flex-col flex-1 pl-8 py-2 gap-3 lg:gap-4 justify-between">
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <div>
                            <span className="inline-block bg-[rgba(25,207,122,0.1)] text-[#00c896] text-xs lg:text-sm font-extrabold rounded-xl px-3 py-1.5">
                                대표작
                            </span>
                        </div>
                        <h3 className="text-[#101828] text-xl md:text-2xl lg:text-[32px] font-extrabold leading-snug line-clamp-2 break-words">
                            {firstBook.bookTitle}
                        </h3>
                        {firstBook.bookDescription && (
                            <p className="text-[#4a5565] text-sm lg:text-[17px] leading-relaxed line-clamp-2 break-words">
                                {firstBook.bookDescription}
                            </p>
                        )}
                    </div>

                    <div className={"flex flex-row justify-center gap-2"}>

                        <div
                            className={"flex flex-row gap-2 bg-[#F9FAFB] py-2 px-8 w-full rounded-md items-center justify-center"}>

                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                 fill="none">
                                <g clipPath="url(#clip0_6483_907)">
                                    <path d="M5.83398 8.33325V18.3333" stroke="#19CF7A" strokeWidth="1.66667"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                    <path
                                        d="M12.4993 4.90008L11.666 8.33341H16.5243C16.7831 8.33341 17.0383 8.39366 17.2697 8.50937C17.5011 8.62508 17.7024 8.79309 17.8577 9.00008C18.0129 9.20707 18.1178 9.44737 18.1641 9.70194C18.2104 9.95651 18.1968 10.2184 18.1243 10.4667L16.1827 17.1334C16.0817 17.4796 15.8712 17.7837 15.5827 18.0001C15.2942 18.2165 14.9433 18.3334 14.5827 18.3334H3.33268C2.89065 18.3334 2.46673 18.1578 2.15417 17.8453C1.84161 17.5327 1.66602 17.1088 1.66602 16.6667V10.0001C1.66602 9.55805 1.84161 9.13413 2.15417 8.82157C2.46673 8.50901 2.89065 8.33341 3.33268 8.33341H5.63268C5.94275 8.33325 6.24663 8.24659 6.51014 8.08318C6.77366 7.91977 6.98637 7.68609 7.12435 7.40841L9.99935 1.66675C10.3923 1.67161 10.7791 1.76522 11.1309 1.94058C11.4826 2.11593 11.7901 2.3685 12.0305 2.67942C12.2709 2.99033 12.4379 3.35155 12.5191 3.73608C12.6003 4.12062 12.5936 4.51853 12.4993 4.90008Z"
                                        stroke="#19CF7A" strokeWidth="1.66667" strokeLinecap="round"
                                        strokeLinejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_6483_907">
                                        <rect width="20" height="20" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                            {firstBook.likeCount ?? 0}
                        </div>


                        <div
                            className={"flex flex-row gap-2 bg-[#F9FAFB] w-full py-2 px-8 rounded-md items-center justify-center"}>

                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                 fill="none">
                                <path
                                    d="M1.7181 10.2901C1.64865 10.103 1.64865 9.89715 1.7181 9.71006C2.39452 8.06993 3.5427 6.66759 5.01708 5.6808C6.49146 4.69402 8.22564 4.16724 9.99977 4.16724C11.7739 4.16724 13.5081 4.69402 14.9825 5.6808C16.4568 6.66759 17.605 8.06993 18.2814 9.71006C18.3509 9.89715 18.3509 10.103 18.2814 10.2901C17.605 11.9302 16.4568 13.3325 14.9825 14.3193C13.5081 15.3061 11.7739 15.8329 9.99977 15.8329C8.22564 15.8329 6.49146 15.3061 5.01708 14.3193C3.5427 13.3325 2.39452 11.9302 1.7181 10.2901Z"
                                    stroke="#19CF7A" strokeWidth="1.66667" strokeLinecap="round"
                                    strokeLinejoin="round"/>
                                <path
                                    d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                                    stroke="#19CF7A" stroke-width="1.66667" stroke-linecap="round"
                                    stroke-linejoin="round"/>
                            </svg>
                            {firstBook.viewCount ?? 0}
                        </div>
                    </div>


                    <Link
                        to={`/book/bookdetails/${firstBook.bookId}`}
                        className="w-full h-10 lg:h-[41px] flex items-center justify-center bg-[rgba(25,207,122,0.1)] text-[#00c896] text-sm lg:text-base font-bold rounded-xl hover:bg-[#19cf7a] hover:text-white transition-colors"
                    >
                        자세히 보기
                    </Link>
                </div>
            </motion.div>

            {/* Remaining books */}
            {restBooks.length > 0 && (
                <div className="grid grid-cols-2 gap-4 lg:gap-5">
                    {displayedRest.map((book: any, idx: number) => (
                        <motion.div 
                            key={idx} 
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay: (idx % 2) * 0.1 }}
                            className="flex bg-white border border-[#e5e7eb] rounded-3xl shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] overflow-hidden flex-row p-4 lg:p-5 gap-4">
                                <Link to={`/book/bookdetails/${book.bookId}`} className="flex-shrink-0 block group">
                                    <div className="w-[100px] h-[136px] lg:w-[120px] lg:h-[160px] rounded-2xl overflow-hidden bg-[#f3f4f6] shadow-[inset_0px_2px_4px_0px_rgba(0,0,0,0.05)]">
                                        <img
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            src={coverSrc(book)}
                                            alt={book.bookTitle}
                                        />
                                    </div>
                                </Link>
                                <div className="flex flex-col justify-between flex-1 min-w-0 py-1">
                                    <div className="flex flex-col gap-1.5">
                                        <h4 className="text-[#101828] text-lg lg:text-[20px] font-bold leading-snug line-clamp-1 break-words">
                                            {book.bookTitle}
                                        </h4>
                                        {book.bookDescription && (
                                            <p className="text-[#6a7282] text-sm lg:text-[15px] leading-relaxed line-clamp-2 break-words">
                                                {book.bookDescription}
                                            </p>
                                        )}
                                    </div>


                                    <div className={"flex flex-row items-center justify-between mt-auto pt-3"}>
                                        <div className={"flex flex-row gap-4 items-center"}>
                                            <div className={"flex flex-row gap-1.5 items-center text-[#6a7282] text-sm lg:text-[15px] font-medium"}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M12.6626 9.33105C13.6557 8.35796 14.6621 7.19158 14.6621 5.66528C14.6621 4.69306 14.2759 3.76066 13.5884 3.07319C12.901 2.38573 11.9686 1.99951 10.9963 1.99951C9.82329 1.99951 8.99683 2.33276 7.99707 3.33252C6.99731 2.33276 6.17085 1.99951 4.9978 1.99951C4.02558 1.99951 3.09318 2.38573 2.40571 3.07319C1.71825 3.76066 1.33203 4.69306 1.33203 5.66528C1.33203 7.19824 2.33179 8.36462 3.33154 9.33105L7.99707 13.9966L12.6626 9.33105Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                {book.likeCount ?? 0}
                                            </div>

                                            <div className={"flex flex-row gap-1.5 items-center text-[#6a7282] text-sm lg:text-[15px] font-medium"}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M1.37369 8.23007C1.31814 8.08043 1.31814 7.91582 1.37369 7.76618C1.91469 6.4544 2.83301 5.3328 4.01223 4.54357C5.19144 3.75433 6.57845 3.33301 7.99741 3.33301C9.41636 3.33301 10.8034 3.75433 11.9826 4.54357C13.1618 5.3328 14.0801 6.4544 14.6211 7.76618C14.6767 7.91582 14.6767 8.08043 14.6211 8.23007C14.0801 9.54184 13.1618 10.6634 11.9826 11.4527C10.8034 12.2419 9.41636 12.6632 7.99741 12.6632C6.57845 12.6632 5.19144 12.2419 4.01223 11.4527C2.83301 10.6634 1.91469 9.54184 1.37369 8.23007Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M7.99756 9.99756C9.10186 9.99756 9.99707 9.10235 9.99707 7.99805C9.99707 6.89375 9.10186 5.99854 7.99756 5.99854C6.89326 5.99854 5.99805 6.89375 5.99805 7.99805C5.99805 9.10235 6.89326 9.99756 7.99756 9.99756Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                {book.viewCount ?? 0}
                                            </div>
                                        </div>

                                        <Link
                                            to={`/book/bookdetails/${book.bookId}`}
                                            className="bg-[rgba(25,207,122,0.1)] text-[#00c896] text-sm lg:text-[15px] font-bold rounded-xl px-4 py-2 hover:bg-[#19cf7a] hover:text-white transition-colors"
                                        >
                                            보기
                                        </Link>
                                    </div>
                                </div>


                        </motion.div>
                    ))}
                </div>
            )}

            {/* 더보기 button */}
            {!showAll && restBooks.length > INITIAL_GRID_COUNT && (
                <button
                    onClick={() => setShowAll(true)}
                    className="w-full h-[52px] lg:h-[60px] bg-white border border-[#d1d5dc] text-[#364153] text-sm lg:text-base font-bold rounded-2xl hover:border-[#19cf7a] hover:text-[#19cf7a] transition-colors"
                >
                    작품 더보기
                </button>
            )}
        </div>
    );
};

export default BooksList;
